import React, { useState, useEffect } from 'react';
import { Loader2, ChevronDown, AlertCircle, AlertTriangle } from 'lucide-react';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../ui/accordion';
import { Badge } from '../ui/badge';
import propertyService from '../../services/propertyService';
import PropertyImageCarouselModal from './PropertyImageCarouselModal';
import { ImageInspectionRegistry, ImageInspectionRegistryItem } from '../../models/property';

interface InspectionImagesDisplayProps {
  propertyId: string;
  inspectionId: string | null;
}

// Define the type outside the component to avoid recreating it on each render
type ImageWithContext = {
  url: string;
  room: string;
  description: string;
  redFlags: string[];
  imageId: string;
};

const InspectionImagesDisplay: React.FC<InspectionImagesDisplayProps> = ({ propertyId, inspectionId }) => {
  // State declarations - keep all hooks at the top level
  const [images, setImages] = useState<ImageWithContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [expandedRooms, setExpandedRooms] = useState<Record<string, boolean>>({});
  
  // Group images by room - optimized and memoized for better performance
  const roomGroups = React.useMemo(() => {
    // Create a map for better performance with large datasets
    const groups: Record<string, ImageWithContext[]> = {};
    
    // Pre-allocate the correct size arrays (optimization)
    // First count how many images per room
    const roomCounts: Record<string, number> = {};
    
    for (let i = 0; i < images.length; i++) {
      const roomName = images[i].room || 'Other';
      roomCounts[roomName] = (roomCounts[roomName] || 0) + 1;
    }
    
    // Create the arrays with the right size
    Object.keys(roomCounts).forEach(room => {
      groups[room] = [];
    });
    
    // Fill the arrays
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const roomName = img.room || 'Other';
      groups[roomName].push(img);
    }
    
    return groups;
  }, [images]);

  // Functions
  // Track which room's images we're viewing
  const [currentRoomName, setCurrentRoomName] = useState<string>('');
  
  // Images for the current room only
  const currentRoomImages = React.useMemo(() => {
    return currentRoomName ? (roomGroups[currentRoomName] || []) : [];
  }, [currentRoomName, roomGroups]);
  
  const openImageModal = (globalIndex: number, roomName: string, localIndex: number) => {
    setCurrentRoomName(roomName);
    setSelectedImageIndex(localIndex);
    setModalOpen(true);
  };

  // Data fetching effect
  useEffect(() => {
    const fetchImages = async () => {
      if (!propertyId || !inspectionId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // Fetch inspection registry to get image IDs
        const inspectionRegistry = await propertyService.getPropertyInspectionRegistry(propertyId) as ImageInspectionRegistry;
        
        if (!inspectionRegistry || !inspectionRegistry.data || inspectionRegistry.data.length === 0) {
          setError('No images found for this inspection');
          setLoading(false);
          return;
        }
        
        // Get only items with images
        const itemsWithImages = inspectionRegistry.data
          .filter((item: ImageInspectionRegistryItem) => item.imageId);
        
        if (itemsWithImages.length === 0) {
          setError('No images found for this inspection');
          setLoading(false);
          return;
        }
        
        // Create array of images with their context
        const imagesWithContext: ImageWithContext[] = [];
        
        // Fetch URLs for each image and combine with context data
        for (const item of itemsWithImages) {
          if (!item.imageId) continue;
          
          try {
            const url = await propertyService.getPropertyImageUrl(propertyId, item.imageId);
            
            if (url) {
              imagesWithContext.push({
                url,
                room: item.Room || 'Unknown Room',
                description: item.Description || 'No description available',
                redFlags: item.RedFlags || [],
                imageId: item.imageId
              });
            }
          } catch (e) {
            console.error(`Error fetching image URL for ${item.imageId}:`, e);
          }
        }
        
        if (imagesWithContext.length === 0) {
          setError('Failed to load inspection images');
        } else {
          setImages(imagesWithContext);
        }
      } catch (e) {
        console.error('Error fetching inspection images:', e);
        setError('Failed to load inspection images');
      } finally {
        setLoading(false);
      }
    };
    
    fetchImages();
  }, [propertyId, inspectionId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">No images available for this inspection</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h3 className="text-xl font-medium mb-4">Inspection Images ({images.length})</h3>
      
      {/* Images grouped by room in collapsible sections */}
      <Accordion type="multiple" className="space-y-2">
        {Object.entries(roomGroups).map(([roomName, roomImages]) => {
          // Count images with issues in this room (not total issues to avoid excessive counts)
          const imagesWithIssues = roomImages.filter(img => img.redFlags && img.redFlags.length > 0).length;
          
          return (
            <AccordionItem 
              key={roomName} 
              value={roomName}
              className="border border-gray-200 rounded-md overflow-hidden"
            >
              <AccordionTrigger className="px-4 py-3 hover:bg-gray-50">
                <div className="flex items-center justify-between w-full pr-4">
                  <span className="font-medium">{roomName}</span>
                  <div className="flex items-center gap-3">
                    {/* Removed issues badge for now */}
                    <span className="text-xs text-gray-500">
                      {roomImages.length} {roomImages.length === 1 ? 'image' : 'images'}
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              
              <AccordionContent className="p-2 bg-gray-50">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {roomImages.map((image, idx) => {
                    // Use the index from the images array directly rather than findIndex for better performance
                    const globalIndex = images.findIndex(img => img.imageId === image.imageId);
                    
                    return (
                      <div 
                        key={idx} 
                        className="relative cursor-pointer rounded-md border border-gray-200 overflow-hidden h-16 bg-white transition-transform hover:scale-[1.02] hover:shadow-sm"
                        onClick={() => openImageModal(globalIndex, roomName, idx)}
                      >
                        {/* Thumbnail Image */}
                        <img
                          src={image.url}
                          alt={`${image.room}`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Remove the individual image issue indicators */}
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
      
      {/* Room-specific Full-screen Image Viewer */}
      <PropertyImageCarouselModal 
        imageUrls={currentRoomImages.map(img => img.url)}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        initialIndex={selectedImageIndex}
        imageInfos={currentRoomImages.map(img => ({
          room: img.room,
          description: img.description,
          redFlags: img.redFlags
        }))}
        roomName={currentRoomName}
      />
    </div>
  );
};

export default InspectionImagesDisplay;